/**
 * 试卷难度分析 - 文件下载工具
 * 由于通用 request 工具默认期望 JSON 响应，无法处理二进制流，
 * 这里使用 axios + responseType:'blob' 单独实现导出下载。
 *
 * 用法（在 detail.vue 或 result.vue 中）：
 *   import { downloadAnalysisExcel, downloadAnalysisPdf } from '@/http/modules/examSet/paperAnalysisDownload'
 *   downloadAnalysisExcel(resultId)
 *   downloadAnalysisPdf(resultId)
 */
import axios from 'axios'
import qs from 'qs'
import Vue from 'vue'
import { paperAnalysisExportExcel, paperAnalysisExportPdf } from '@/http/api'

function getToken() {
  try {
    return (
      localStorage.getItem('token') ||
      localStorage.getItem('Authorization') ||
      sessionStorage.getItem('token') ||
      ''
    )
  } catch (e) {
    return ''
  }
}

/**
 * 解析后端返回 Content-Disposition 中的文件名（兼容 filename* / filename）
 */
function parseFileName(disposition, fallback) {
  if (!disposition) return fallback
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1])
    } catch (e) {
      // ignore
    }
  }
  const plain = /filename=([^;]+)/i.exec(disposition)
  if (plain && plain[1]) {
    let name = plain[1].trim().replace(/^"+|"+$/g, '')
    try {
      return decodeURIComponent(name)
    } catch (e) {
      return name
    }
  }
  return fallback
}

/**
 * 触发浏览器下载
 */
function triggerDownload(blob, fileName) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => window.URL.revokeObjectURL(url), 1000)
}

/**
 * 当后端返回的 blob 实际为错误 JSON 时，尝试解析并以消息形式抛出。
 */
function tryParseErrorBlob(blob) {
  return new Promise((resolve) => {
    if (!blob) return resolve(null)
    if (blob.type && blob.type.indexOf('application/json') === -1) {
      return resolve(null)
    }
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result)
        resolve(json)
      } catch (e) {
        resolve(null)
      }
    }
    reader.onerror = () => resolve(null)
    reader.readAsText(blob)
  })
}

function exportAnalysis(url, params, defaultName, suffix) {
  const $event = Vue.prototype.$event
  let loadingInstance = null
  try {
    if ($event && $event.$loading) {
      loadingInstance = $event.$loading({ background: 'transparent' })
    }
  } catch (e) {
    // ignore
  }
  const token = getToken()
  return axios({
    url,
    method: 'POST',
    data: qs.stringify(params || {}),
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: token
    },
    timeout: 5 * 60 * 1000
  })
    .then(async (resp) => {
      const data = resp.data
      // 错误兜底：blob 内为 JSON 表示出错
      const errJson = await tryParseErrorBlob(data)
      if (errJson) {
        const msg = (errJson && (errJson.msg || errJson.message)) || '导出失败'
        if ($event && $event.$message) {
          $event.$message.error(msg)
        }
        throw new Error(msg)
      }
      const fileName = parseFileName(
        resp.headers && (resp.headers['content-disposition'] || resp.headers['Content-Disposition']),
        defaultName + '.' + suffix
      )
      triggerDownload(data, fileName)
      return true
    })
    .catch((err) => {
      if ($event && $event.$message) {
        $event.$message.error('导出失败：' + (err && err.message ? err.message : '未知错误'))
      }
      throw err
    })
    .finally(() => {
      if (loadingInstance && loadingInstance.close) {
        loadingInstance.close()
      }
    })
}

export function downloadAnalysisExcel(resultId, fileName) {
  return exportAnalysis(
    paperAnalysisExportExcel,
    { resultId },
    fileName || '试卷难度分析',
    'xlsx'
  )
}

export function downloadAnalysisPdf(resultId, fileName) {
  return exportAnalysis(
    paperAnalysisExportPdf,
    { resultId },
    fileName || '试卷难度分析报告',
    'pdf'
  )
}

export default {
  downloadAnalysisExcel,
  downloadAnalysisPdf
}
